import type { I18nDto } from '@bldsoft/cappie/api/wbs'
import type { HttpRequest, HttpResult } from '@bldsoft/cappie/transport/http'
import type {
  CacheEntry,
  CacheRule,
  CacheStore,
} from '@bldsoft/cappie/transport/http/cache'
import type { GenericApiResponse } from '@setplex/tria-api'

type ApiNoraTranslations = GenericApiResponse<{
  content?: readonly I18nDto[]
}>

/**
 * Tag of every cached response which belongs to the current subscriber
 * or profile. The app drops them when either changes, because cache keys
 * are URL shaped and carry no session of their own.
 */
export const SUBSCRIBER_CACHE_TAG = 'subscriber'

/**
 * Per call cache options of a subscriber scoped response
 */
export function subscriberCache(ttl: number) {
  return { strategy: 'cache-first', ttl, tags: [SUBSCRIBER_CACHE_TAG] } as const
}

/**
 * Nora answers an unchanged `/v3/i18n` either with a plain `304`,
 * or with a `WG0304` business error on a `200`
 */
const NOT_MODIFIED = 'WG0304'

/**
 * Parse a transport body, which is still raw at this level
 */
function decode(result: HttpResult): ApiNoraTranslations | undefined {
  if (!('body' in result) || result.body.byteLength === 0) return

  try {
    return JSON.parse(new TextDecoder().decode(result.body))
  } catch (ignore) {
    return
  }
}

/**
 * Timestamp of the translations, sent back on the next request to let Nora
 * answer `WG0304` when nothing changed. All locales share one timestamp.
 */
function timestampOf(json?: ApiNoraTranslations): number | undefined {
  return json?.payload?.content?.[0]?.timestamp
}

function timestampIn(entry: CacheEntry | null): number | undefined {
  const timestamp = entry?.meta?.timestamp
  return typeof timestamp === 'number' ? timestamp : undefined
}

/**
 * Cache translations until Nora reports them changed.
 *
 * There is no `ttl`, freshness is the backend's call: every request carries
 * the timestamp of the cached copy, and an unchanged answer keeps that copy.
 * Needs a durable store to survive a restart.
 */
export function i18nCacheRule(store: CacheStore): CacheRule {
  return {
    match: { paths: ['**/api/v3/i18n'], methods: ['GET'] },
    strategy: 'network-first',
    store,

    hooks: {
      // the entity never sends a timestamp, and the key is built before this
      // hook runs, so the key stays stable without a `key` hook
      request(request: HttpRequest, entry: CacheEntry | null) {
        const timestamp = timestampIn(entry)
        if (timestamp == null) return request

        const separator = request.url.includes('?') ? '&' : '?'
        return {
          ...request,
          url: `${request.url}${separator}timestamp=${timestamp}`,
        }
      },

      response(result: HttpResult, entry: CacheEntry | null) {
        if (entry != null && 'status' in result && result.status === 304) {
          return { notModified: true }
        }

        const json = decode(result)

        if (
          entry != null &&
          json?.status === 'ERROR' &&
          json.errorCode === NOT_MODIFIED
        ) {
          return { notModified: true }
        }

        const timestamp = timestampOf(json)
        return timestamp == null ? undefined : { meta: { timestamp } }
      },
    },
  }
}
